import { useState } from 'react';

import { cn } from '~/lib/cn';

export type TabsTheme = 'default' | 'toggler';

export interface TabItem {
  id: string;
  name: string;
  hidden?: boolean;
}

export interface TabsProps {
  className?: string;
  items: TabItem[];
  onChange: (item: TabItem) => void;
  theme?: TabsTheme;
  defaultTab?: string;
  loading?: boolean;
}

interface ThemeClasses {
  ul: string;
  li: string;
  liActive: string;
  liInactive: string;
}

const themeClasses: Record<TabsTheme, ThemeClasses> = {
  default: {
    ul: cn('inline-flex w-full select-none items-baseline gap-sm'),
    li: cn(
      'w-full border-x border-t px-md py-lg text-body-sm font-bold border-emphasis-subtle text-default',
    ),
    liActive: cn(
      'z-10 -mb-xs rounded-l-sm rounded-r-sm rounded-t-sm pb-2xl bg-level-02 text-default',
    ),
    liInactive: cn('cursor-pointer rounded-sm border border-emphasis-subtle'),
  },
  toggler: {
    ul: cn(
      'inline-flex h-[40px] select-none items-center rounded-sm px-sm bg-default',
    ),
    li: cn('h-[32px] px-[0.84em] text-body-sm font-bold text-default'),
    liActive: cn(
      'rounded-sm border-t bg-level-05 border-emphasis-subtle text-tertiary-default',
    ),
    liInactive: cn('cursor-pointer'),
  },
};

export function Tabs({
  className,
  items,
  theme,
  defaultTab,
  loading,
  onChange,
}: TabsProps) {
  const [activeTab, setActiveTab] = useState<string>(defaultTab ?? items[0].id);

  const handleClick = (item: TabItem): void => {
    setActiveTab(item.id);
    onChange(item);
  };

  const currentTheme = themeClasses[theme ?? 'default'];

  return (
    <ul className={cn(currentTheme.ul, className)}>
      {items
        .filter((item) => !item.hidden)
        .map((item, index) => {
          const isActive = activeTab === item.id;

          return (
            <li
              className={cn(
                currentTheme.li,
                isActive ? currentTheme.liActive : currentTheme.liInactive,
                loading && 'cursor-default',
              )}
              key={index}
              data-testid={`tab-item-${item.id}`}
            >
              <button
                data-testid={`tab-btn-${item.id}`}
                onClick={() => handleClick(item)}
                disabled={loading || isActive}
                className="m-0 size-full p-0 text-left"
              >
                {theme === 'default' ? (
                  <div
                    className={cn(
                      'mb-md h-xs w-full rounded-full bg-emphasis-default',
                      { 'shadow-yellow bg-tertiary-default': isActive },
                    )}
                  />
                ) : null}
                <span>{item.name}</span>
              </button>
            </li>
          );
        })}
    </ul>
  );
}

Tabs.displayName = 'Tabs';
